import { jwtDecode } from 'jwt-decode'

import { STORAGE_KEYS } from '@/config/storageKeys'
import { getCurrentUser } from '@/services/auth/authApi'
import { refreshCoordinator } from '@/services/http/refreshCoordinator'
import { useAuthStore, type UserInfo } from '@/stores/authStore'
import { logger } from '@myorg/type-lib'
import { redirectToClientLogin, redirectToClientLogout } from '../redirect/authRedirect'
import { appAuth } from '../platform/appAuth'
import { useAuthAdapter } from '../platform/authAdapter'

let checkAuthPromise: Promise<boolean> | null = null

type LoginTokens = {
  access_token: string
  refresh_token?: string
}

const normalizeRoles = (roles: unknown): string[] => {
  if (Array.isArray(roles)) return roles.map(String)
  if (typeof roles === 'string' && roles.trim()) {
    return roles.split(',').map(role => role.trim()).filter(Boolean)
  }
  return []
}

const toUserInfo = (source: any): UserInfo => ({
  userId: source?.userId || source?.sub || '',
  merchantId: source?.merchantId || '',
  name: source?.username || source?.name || source?.sub || '',
  roles: normalizeRoles(source?.roles),
})

const persistUserToLocalStorage = (user: UserInfo) => {
  if (user.userId) localStorage.setItem(STORAGE_KEYS.USER_ID, user.userId)
  if (user.merchantId) localStorage.setItem(STORAGE_KEYS.MERCHANT_ID, user.merchantId)
  if (user.name) localStorage.setItem(STORAGE_KEYS.USERNAME, user.name)
  localStorage.setItem(STORAGE_KEYS.USER_ROLES, JSON.stringify(user.roles))
}

const clearUserFromLocalStorage = () => {
  localStorage.removeItem(STORAGE_KEYS.USER_ID)
  localStorage.removeItem(STORAGE_KEYS.MERCHANT_ID)
  localStorage.removeItem(STORAGE_KEYS.USERNAME)
  localStorage.removeItem(STORAGE_KEYS.USER_ROLES)
}

const resolveCurrentUser = async (tokens?: LoginTokens, user?: any): Promise<UserInfo> => {
  if (user) return toUserInfo(user)

  if (tokens?.access_token) {
    return toUserInfo(jwtDecode(tokens.access_token))
  }

  const currentUser = await getCurrentUser()
  return toUserInfo(currentUser)
}

const updateUserState = async (
  authStore: ReturnType<typeof useAuthStore>,
  adapter: ReturnType<typeof useAuthAdapter>,
  user: UserInfo
) => {
  await adapter.setUser(user)
  persistUserToLocalStorage(user)
  authStore.setUser(user)
  authStore.setLoginStatus(true)
}

const resetUserState = async (
  authStore: ReturnType<typeof useAuthStore>,
  adapter: ReturnType<typeof useAuthAdapter>,
  options?: { isForce?: boolean }
) => {
  checkAuthPromise = null
  authStore.clearAuthData()
  authStore.clearMessages()
  clearUserFromLocalStorage()

  if (options?.isForce) {
    authStore.setError('登入逾時，請重新登入')
  } else {
    authStore.setMessage('已登出')
  }

  await adapter.logout()
}

export const useAuthFacade = () => {
  const authStore = useAuthStore()
  const adapter = useAuthAdapter()

  const checkAuth = async (): Promise<boolean> => {
    if (authStore.isLoggedIn) {
      return true
    }

    if (checkAuthPromise) {
      return checkAuthPromise
    }

    checkAuthPromise = (async () => {
      try {
        const result = await adapter.checkAuth()

        if (result.success && result.user) {
          await updateUserState(authStore, adapter, result.user)
          return true
        }

        authStore.setUser(null)
        authStore.setLoginStatus(false)
        return false
      } catch (err) {
        logger.error('[authFacade.checkAuth] failed', err)
        authStore.setUser(null)
        authStore.setLoginStatus(false)
        return false
      } finally {
        checkAuthPromise = null
      }
    })()

    return checkAuthPromise
  }

  const login = async () => {
    await adapter.login()
    redirectToClientLogin()
  }

  const handleLoginSuccess = async (
    tokens?: LoginTokens,
    user?: any
  ): Promise<UserInfo> => {
    logger.log('[authFacade.handleLoginSuccess] start')

    if (tokens) {
      await appAuth.setTokens(tokens)
    }

    const resolvedUser = await resolveCurrentUser(tokens, user)
    await updateUserState(authStore, adapter, resolvedUser)

    logger.log('[authFacade.handleLoginSuccess] completed', resolvedUser)
    return resolvedUser
  }

  const doLogout = async (
    options?: { isForce?: boolean }
  ): Promise<void> => {
    await resetUserState(authStore, adapter, options)
    redirectToClientLogout()
  }

  const logout = async (
    options?: { isForce?: boolean }
  ): Promise<void> => {
    return refreshCoordinator.runLogout(async () => {
      await doLogout(options)
    })
  }

  return {
    checkAuth,
    login,
    logout,
    handleLoginSuccess,

    get user() {
      return authStore.user
    },

    get isLoggedIn() {
      return authStore.isLoggedIn
    },
  }
}
